import PrivacyCheckbox from '@components/PrivacyCheckbox/PrivacyCheckbox';
import Spinner from '@components/Spinner/Spinner';
import UploadField from '@components/UploadField/UploadField';
import ValidatedField from '@components/ValidatedField/ValidatedField';
import logger from '@utils/logger';
import { FormEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CAREERS_FORM_ENDPOINT, MAX_FILE_SIZE } from 'src/constants';
import send from 'src/static/vectors/contact-send.svg';
import { FormStatus, JobFormData } from 'src/types';
import './CareersForm.css';

const CareersForm = ({ jobId }: { jobId?: string }) => {
	const [formStatus, setFormStatus] = useState<FormStatus>('idle');
	const intl = useIntl();

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		const resumeFile = formData.get('resume') as File | null;
		setFormStatus('loading');
		if (!resumeFile || !(resumeFile instanceof File) || resumeFile.size > MAX_FILE_SIZE) {
			setFormStatus('error');
			return;
		}
		try {
			const fileReader = new FileReader();
			const fileBase64 = await new Promise<string>((resolve, reject) => {
				fileReader.onload = () => resolve(fileReader.result as string);
				fileReader.onerror = () => reject(new Error('Error al leer el archivo'));
				fileReader.readAsDataURL(resumeFile);
			});
			const jobFormData: JobFormData = {
				name: formData.get('name') as string,
				lastname: formData.get('lastname') as string,
				email: formData.get('email') as string,
				linkedin: formData.get('linkedin') as string,
				message: formData.get('message') as string,
				resume: {
					name: resumeFile.name,
					type: resumeFile.type,
					size: resumeFile.size,
					data: fileBase64
				},
				subject: jobId ? `Contact from job id:${jobId}` : 'Contact from careers page'
			};
			const response = await fetch(CAREERS_FORM_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(jobFormData)
			});
			if (!response.ok) {
				throw new Error(`Error: ${response.status} - ${response.statusText}`);
			}
			const result = await response.json();
			logger.info({ result, jobId }, 'careers form submitted');
			setFormStatus('success');
		} catch (error) {
			logger.error({ err: error, jobId }, 'careers form submission failed');
			setFormStatus('error');
		}
	};

	return (
		<div id="careers-form">
			{formStatus === 'success' ? (
				<div className="success-feedback">
					<img className="symbol" src={send} alt="form sent" />
					<h1>
						<FormattedMessage id="send-success" />
					</h1>
				</div>
			) : (
				<form onSubmit={handleSubmit}>
					<fieldset id="top-fieldset">
						<ValidatedField
							name="name"
							type="text"
							placeholder={intl.formatMessage({ id: 'placeholder.name' }) + '*'}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<ValidatedField
							name="lastname"
							type="text"
							placeholder={intl.formatMessage({ id: 'placeholder.lastname' }) + '*'}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<ValidatedField
							name="email"
							type="email"
							placeholder={intl.formatMessage({ id: 'placeholder.email' }) + '*'}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<input
							type="text"
							name="linkedin"
							pattern="^(https?:\/\/)?(www\.)?linkedin\.com\/.+$"
							placeholder={intl.formatMessage({ id: 'placeholder.linkedin' })}
							title="ej: linkedin.com/in/..."
						/>
						<textarea rows={6} minLength={10} name="message" placeholder={intl.formatMessage({ id: 'placeholder.message' })} />
						<UploadField required name="resume" title={<FormattedMessage id="placeholder.careers-file" />} />
					</fieldset>
					<fieldset id="bottom-fieldset">
						<PrivacyCheckbox />
					</fieldset>
					<div className="feedback">
						{formStatus === 'error' && (
							<strong className="error error-text">
								<FormattedMessage id="send-error" />
							</strong>
						)}
					</div>
					<button type="submit" disabled={formStatus !== 'idle'}>
						{formStatus !== 'loading' ? <FormattedMessage id="send" /> : <Spinner />}
					</button>
				</form>
			)}
		</div>
	);
};

export default CareersForm;
